/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1779235200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed May 20 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "google-campaign-manager",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Google Campaign Manager 360",
          "depth": 0,
          "id": "connect-to-google-campaign-manager-360"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing conversion events",
          "depth": 1,
          "id": "syncing-conversion-events"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      strong: "strong",
      p: "p",
      h3: "h3",
      img: "img",
      h4: "h4",
      code: "code",
      ul: "ul",
      li: "li"
    }, _provideComponents(), props.components), {PremiumDestination, EventSyncWarning, CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!PremiumDestination) _missingMdxReference("PremiumDestination", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(PremiumDestination, {
        destinationName: "Google Campaign Manager 360"
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported sync modes"
            }), _jsx(_components.th, {
              children: "API reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#syncing-conversion-events",
                children: _jsx(_components.strong, {
                  children: "Conversions"
                })
              })
            }), _jsx(_components.td, {
              children: "Sync records from your model as new conversion events"
            }), _jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developers.google.com/doubleclick-advertisers/rest/v4/conversions/batchinsert",
                children: "Conversions docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#syncing-conversion-events",
                children: _jsx(_components.strong, {
                  children: "Conversion Adjustments"
                })
              })
            }), _jsx(_components.td, {
              children: "Sync records from your model to existing conversion events"
            }), _jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developers.google.com/doubleclick-advertisers/rest/v4/conversions/batchupdate",
                children: "Conversion Adjustments docs"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-google-campaign-manager-360",
        children: "Connect to Google Campaign Manager 360"
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["You need to have access to at least one ", _jsx(_components.a, {
            href: "https://support.google.com/campaignmanager/answer/6098287?hl=en&sjid=13478745075182123470-NA",
            children: "user profile"
          }), " and one ", _jsx(_components.a, {
            href: "https://support.google.com/campaignmanager/answer/2829356?hl=en&sjid=13478745075182123470-NA",
            children: "advertiser"
          }), " in Google Marketing Platform to sync data."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Google Campaign Manager 360"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "Google Campaign Manager 360"
        }), " via OAuth."]
      }), "\n", _jsxs(_components.p, {
        children: ["For the ", _jsx(_components.strong, {
          children: "Authentication method"
        }), ", select ", _jsx(_components.strong, {
          children: "Log in to Google Campaign Manager 360"
        }), " and log into your Google Campaign Manager 360 account. Once successful, you will be redirected back to Hightouch to enter a descriptive name for your destination and complete setup."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Google Campaign Manager 360 destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Google Campaign Manager 360 destination you want to sync to."]
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsx(_components.h3, {
        id: "syncing-conversion-events",
        children: "Syncing conversion events"
      }), "\n", _jsxs(_components.p, {
        children: ["You can choose to sync new conversion events or update existing ones via the ", _jsx(_components.strong, {
          children: "Conversion Adjustments"
        }), " sync type."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-google-campaign-manager-sync-types.png",
          alt: "Sync configuration in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "Then, select the Google Campaign Manager profile to sync conversion data to."
      }), "\n", _jsx(_components.h4, {
        id: "floodlight-activities-and-configuration",
        children: "Floodlight activities and configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Google Campaign Manager requires each conversion to be associated with a ", _jsx(_components.a, {
          href: "https://support.google.com/campaignmanager/answer/3027419?hl=en&sjid=13478745075182123470-NA",
          children: "Floodlight activity"
        }), " and ", _jsx(_components.a, {
          href: "https://support.google.com/campaignmanager/answer/2893922",
          children: "configuration"
        }), ".\nHightouch provides a dropdown to select the available activities and configurations based on your Google Campaign Manager profile.\nYou can either select a static value for all events or select to ", _jsx(_components.strong, {
          children: "use a column"
        }), " from your model."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-google-campaign-manager-columns.png",
          alt: "Sync configuration in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h4, {
        id: "user-advertising-identifiers",
        children: "User advertising identifiers"
      }), "\n", _jsxs(_components.p, {
        children: ["You can opt to include encrypted user advertising IDs in your conversion event payload.\nHightouch obtains encrypted user advertising IDs from the ", _jsx(_components.a, {
          href: "https://developers.google.com/doubleclick-advertisers/guides/conversions_overview#using_the_m_match_macro",
          children: "%m match macro"
        }), " or ", _jsx(_components.a, {
          href: "https://developers.google.com/doubleclick-advertisers/guides/conversions_overview#using_data_transfer",
          children: "Data Transfer"
        }), ".\nIf you are including, ", _jsx(_components.code, {
          children: "dclid"
        }), ", ", _jsx(_components.code, {
          children: "gclid"
        }), ", ", _jsx(_components.code, {
          children: "matchId"
        }), ", or a mobile device ID in your ", _jsx(_components.a, {
          href: "#field-mapping",
          children: "field mapping"
        }), ", you don't need to enable this."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Field mapping is where you select which customer information parameters or other event parameters you want to send to the Conversions API.\nYou can map data from any of your model columns to default and custom Google fields."
      }), "\n", _jsx(_components.p, {
        children: "The Conversions API requires you to include at least one of the user identifier parameters below in your event payload, so be sure to map one here.\nThe identifiers options are:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Mobile device ID"
        }), "\n", _jsx(_components.li, {
          children: "Google Click ID"
        }), "\n", _jsx(_components.li, {
          children: "Match ID"
        }), "\n", _jsx(_components.li, {
          children: "Display Click ID"
        }), "\n", _jsx(_components.li, {
          children: "Encrypted user ID"
        }), "\n", _jsx(_components.li, {
          children: "Encrypted user ID candidates"
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "enhanced-conversions-mappings",
        children: "Enhanced conversions mappings"
      }), "\n", _jsxs(_components.p, {
        children: ["The CM360 offline conversion API supports ", _jsx(_components.a, {
          href: "https://developers.google.com/doubleclick-advertisers/guides/conversions_ec",
          children: "enhanced conversions"
        }), " with additional user identifiers. To send enhanced conversions, you can map data from any of your model columns to the following user identifiers:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Hashed email"
        }), "\n", _jsx(_components.li, {
          children: "Hashed phone number"
        }), "\n", _jsx(_components.li, {
          children: "Hashed first name"
        }), "\n", _jsx(_components.li, {
          children: "Hashed last name"
        }), "\n", _jsx(_components.li, {
          children: "Hashed street address"
        }), "\n", _jsx(_components.li, {
          children: "City"
        }), "\n", _jsx(_components.li, {
          children: "State"
        }), "\n", _jsx(_components.li, {
          children: "Country code"
        }), "\n", _jsx(_components.li, {
          children: "Postal code"
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["You should provide ", _jsx(_components.strong, {
            children: "only one"
          }), " of these user identifiers for matching."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["For more information, please check the ", _jsx(_components.a, {
          href: "https://developers.google.com/doubleclick-advertisers/rest/v3.5/Conversion",
          children: "Google Campaign Manager 360 documentation"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.p, {
        children: "When uploading offline conversions CM360 enforces a 28 day window, therefore, any conversion timestamp older than 28 days will be rejected with errors like \"The timestamp is too old\". If you receive this error then you should check to ensure that the timestamp of your conversions is at most 28 days old from the time of submission."
      }), "\n", _jsxs(_components.p, {
        children: ["This information, along with a few other FAQs, can be found on the CM360 Conversions FAQ page ", _jsx(_components.a, {
          href: "https://developers.google.com/doubleclick-advertisers/guides/conversions_faq",
          children: "here"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
